import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useProgramVersionDetails } from "@/Common/hooks/Metrics";

// Helpers
import { getFranchiseOEMProgramVersion, getFranchiseOEMReference, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Layouts
import AdvancedSelectLabel from "@/Layouts/components/InputComponents/AdvancedSelectLabel";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import RequiredReferences from "@/Layouts/components/RequiredReferences";

// Components
import RequiredMetrics from "@PayPlans/components/RequiredMetrics";

// MUI
import { Grid } from "@mui/material";

const MANUAL_INPUT_TYPE = "Manual Input";

const payouts = [
	{ potentialPayout: "From Financial Statement" },
	{ potentialPayout: MANUAL_INPUT_TYPE },
	{ potentialPayout: "From OEM Report" },
];

const hasAllRequiredMetrics = (metrics = []) => metrics.every((metric) => metric.required);

const updateRequiredTiers = (metrics = [], tiers = []) => {
	const hasInitializedMetrics = Boolean(metrics?.length);
	const mandatoryRequired = metrics.filter((metric) => metric.required).length || 0;

	// Required number limits
	const maxNumberRequired = metrics?.length || 0;
	const minNumberRequired = Math.min(maxNumberRequired, mandatoryRequired) || 0;

	// Remove references from the end
	if (tiers.length > maxNumberRequired) {
		tiers.length = maxNumberRequired || 1;
	}

	return tiers.map((tier) => {
		let numberRequired = tier["numberRequired"] || 1;

		if (minNumberRequired && hasInitializedMetrics) {
			if (minNumberRequired === maxNumberRequired) {
				numberRequired = minNumberRequired;
			} else {
				const leastRequired = minNumberRequired + 1;
				const maxCurrent = Math.min(numberRequired, maxNumberRequired);
				numberRequired = Math.max(maxCurrent, leastRequired);
			}
		} else if (numberRequired > maxNumberRequired) {
			numberRequired = maxNumberRequired;
		}

		return { ...tier, numberRequired };
	});
};

const isAllReferencesValid = (
	references = [],
	definition = { missed: "missed", attained: "attained", numberRequired: "numberRequired" }
) => {
	if (!references?.length) {
		return true;
	}
	const { missed, attained, numberRequired } = definition || {};
	return references.every((reference) => (reference[missed]?.value || reference[attained]?.value) && reference[numberRequired]);
};

const mapModelOEMVersion = (model, args) => {
	const { make } = args ?? {};
	const { oemReference = "", version = "" } = getFranchiseOEMProgramVersion({ ...args, franchise: make });
	return {
		...model,
		oemReference,
		version,
	};
};

export default function Excellence(props) {
	const {
		make = "",
		oemReference = "",
		version = "",
		reference = "SFE",
		department = "SALES",
		makeMetrics = {
			potentialPayout: "From Financial Statement",
			manualInput: { input: "Dollar", value: 0 },
			metrics: [],
			numberRequired: 0,
			attained: { input: "Percent", value: 0 },
			missed: { input: "Percent", value: 0 },
		},
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Location
	const location = useSelector((state) => state.location.location);
	const locationOEMs = getSanitatedLocationOEMs(location?.branchesOEMs);

	// Get properly metrics based on program reference
	const localOEMReference = getFranchiseOEMReference({ franchise: make, oemReference, locationOEMs });
	const { data: programDetails, isLoading } = useProgramVersionDetails({
		program: reference,
		oemReference: localOEMReference,
		department,
		version,
	});
	const { details = undefined } = programDetails ?? {};
	const { metrics: metricsOptions = [] } = details ?? {};

	// Control to send update actions
	const wasUpdated = useRef(false);
	const isValid = useRef(true);
	const isValidReferences = useRef(true);

	const onMakeMetricsUpdate = (updatedMetrics) => {
		const validMetrics = isValid.current && isValidReferences.current && Boolean(updatedMetrics?.metrics?.length);
		const isAllValid = validMetrics && isAllReferencesValid(updatedMetrics?.tiers || []);
		onActionValidation?.(make, isAllValid);
		if (isAllValid && wasUpdated.current) {
			wasUpdated.current = false;
			const { oemReference: programOEMReference, version: programVersion } = programDetails ?? {};
			const updatedModel = mapModelOEMVersion(updatedMetrics, {
				make,
				locationOEMs,
				oemReference: programOEMReference,
				version: programVersion,
			});
			onActionUpdate(make, updatedModel);
		}
	};

	// States
	const [innerMakeMetrics, setMakeMetrics] = useCallbackState(null, onMakeMetricsUpdate);

	const handleChangeTiers = (tiers, action) => {
		const updated = { ...innerMakeMetrics };
		const updatedTiers = updateRequiredTiers(updated["metrics"], tiers);
		updated["tiers"] = [...updatedTiers];

		if (action === "adding") {
			setMakeMetrics(updated);
		} else {
			saveMakeMetrics(updated);
		}
	};

	const handleUpdatePayout = (value) => {
		const updated = { ...innerMakeMetrics };
		if (updated?.["potentialPayout"] !== value?.potentialPayout) {
			updated["potentialPayout"] = value?.potentialPayout;
			if (value?.potentialPayout !== MANUAL_INPUT_TYPE) {
				updated["manualInput"] = { input: "Dollar", value: 0 };
			}
			saveMakeMetrics(updated);
		}
	};

	const handleUpdateManualInput = (area, value) => {
		const updated = { ...innerMakeMetrics };
		updated[area]["value"] = value ? Number(value) : 0;
		saveMakeMetrics(updated);
	};

	const handleUpdateMetrics = (updatedMetrics = []) => {
		const updated = { ...innerMakeMetrics };
		const updatedTiers = updateRequiredTiers(updatedMetrics, updated["tiers"]);

		const allRequiredMetrics = hasAllRequiredMetrics(updatedMetrics);
		if (allRequiredMetrics) {
			updatedMetrics = updatedMetrics.map((metric) => {
				return { ...metric, required: false };
			});
		}

		updated["tiers"] = updatedTiers;
		updated["metrics"] = updatedMetrics;
		saveMakeMetrics(updated);
	};

	const saveMakeMetrics = (updated) => {
		wasUpdated.current = true;
		setMakeMetrics(updated);
	};

	useEffect(() => {
		setMakeMetrics(makeMetrics);
		return () => {
			setMakeMetrics(null);
			wasUpdated.current = false;
			isValid.current = true;
			isValidReferences.current = true;
		};
	}, [makeMetrics, setMakeMetrics]);

	return (
		<Grid container direction="column">
			<Grid container item direction={"row"} alignItems={"center"} mt={2.5} xs={12}>
				<Grid item>
					<AdvancedSelectLabel
						label={"Potential Payout"}
						data={payouts}
						selected={innerMakeMetrics?.potentialPayout ? { potentialPayout: innerMakeMetrics.potentialPayout } : null}
						showBy={"potentialPayout"}
						selectBy={"potentialPayout"}
						placeholder={"Select Payout"}
						loading={false}
						readOnly={readOnly}
						sxSelectContainer={{ minWidth: "192px" }}
						onChangeSelection={(value) => handleUpdatePayout(value)}
					/>
				</Grid>
				{Boolean(innerMakeMetrics?.potentialPayout === MANUAL_INPUT_TYPE) && (
					<Grid item ml={3}>
						<InputFormExtension
							data={[
								{
									type: "Dollar",
									min: 0,
									max: 1000000,
									decimals: 2,
									decimalsEditable: false,
								},
							]}
							value={innerMakeMetrics?.manualInput?.value || 0}
							defaultType={innerMakeMetrics?.manualInput?.input || "Dollar"}
							disabled={readOnly}
							readOnly={readOnly}
							sx={{
								width: "95px",
							}}
							onActionUpdate={(value) => handleUpdateManualInput("manualInput", value)}
						/>
					</Grid>
				)}
			</Grid>

			<Grid item mt={2.5}>
				<RequiredMetrics
					options={metricsOptions}
					totalOptions={metricsOptions?.length}
					metrics={innerMakeMetrics?.metrics || []}
					emptyError={true}
					loading={isLoading}
					readOnly={readOnly || isLoading}
					onActionUpdate={(updatedMetrics) => handleUpdateMetrics(updatedMetrics)}
					onActionValidation={(isValidMetrics) => {
						isValid.current = isValidMetrics;
					}}
				/>
			</Grid>

			<RequiredReferences
				metrics={innerMakeMetrics?.metrics || []}
				tiers={innerMakeMetrics?.tiers || []}
				labels={{
					required: "Required",
					above: "Attained",
					below: "Missed",
				}}
				loading={isLoading}
				readOnly={readOnly || isLoading}
				handleChangeTiers={handleChangeTiers}
				onActionValidation={(isReferenceValid) => {
					isValidReferences.current = isReferenceValid;
				}}
			/>
		</Grid>
	);
}
Excellence.propTypes = {
	reference: PropTypes.string,
	department: PropTypes.string,
	make: PropTypes.string,
	oemReference: PropTypes.string,
	version: PropTypes.string,
	makeMetrics: PropTypes.shape({
		potentialPayout: PropTypes.string,
		manualInput: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		metrics: PropTypes.array,
		numberRequired: PropTypes.number,
		attained: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		missed: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
	}),
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
